import React, { Suspense, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { EffectComposer, Bloom, ChromaticAberration, Vignette, Noise } from "@react-three/postprocessing";
import { BlendFunction, KernelSize } from "postprocessing";
import * as THREE from "three";

/* --------------------------------------------------------------------------
   THE VOID — a Gargantua-style gravitationally lensed black hole.

   Approach: a single fullscreen shader that draws the black hole in
   screen space. This is the technique used by cinematic real-time black
   hole demos (Shadertoy, etc.) and it produces the iconic Interstellar
   look — a black disc surrounded by the near-side accretion band with a
   bright wrap-around halo formed by the lensed far side of the disk.

   In addition we render:
     • a starfield behind (parallax)
     • particles being drawn toward the horizon
     • bloom + chromatic aberration + vignette + film grain
-------------------------------------------------------------------------- */

/** Fullscreen shader plane — the black hole is rendered here. */
function BlackHoleSurface() {
  const size = useRef({ w: 1, h: 1 });
  const material = useMemo(() => {
    return new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: false,
      uniforms: {
        uTime: { value: 0 },
        uResolution: { value: new THREE.Vector2(1, 1) },
        uAspect: { value: 1 },
        uFocus: { value: 0 }, // 0 = home, 1 = modal — camera "zooms in"
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main(){
          vUv = uv;
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        varying vec2 vUv;
        uniform float uTime;
        uniform vec2  uResolution;
        uniform float uAspect;
        uniform float uFocus;

        // ---------- noise ----------
        float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7))) * 43758.5453); }
        float noise(vec2 p){
          vec2 i = floor(p); vec2 f = fract(p);
          float a = hash(i);
          float b = hash(i + vec2(1.0,0.0));
          float c = hash(i + vec2(0.0,1.0));
          float d = hash(i + vec2(1.0,1.0));
          vec2 u = f*f*(3.0-2.0*f);
          return mix(a,b,u.x) + (c-a)*u.y*(1.0-u.x) + (d-b)*u.x*u.y;
        }
        float fbm(vec2 p){
          float v = 0.0; float a = 0.5;
          for(int i=0;i<5;i++){ v += a * noise(p); p *= 2.02; a *= 0.5; }
          return v;
        }

        // ---------- disk sampler ----------
        // Given a "disk-plane" point (as if the disk lay flat in the plane
        // orthogonal to the camera), return the emissive colour and alpha.
        // Coordinates are in "sphere radii": the event horizon is at r=1.
        vec4 sampleDisk(vec2 dp, float t){
          float r = length(dp);
          // Disk exists between the ISCO (~2 r_s) and outer radius
          float innerR = 1.05;
          float outerR = 3.2;
          if (r < innerR || r > outerR) return vec4(0.0);

          float ang = atan(dp.y, dp.x);

          // Keplerian differential rotation — inner faster
          float rotSpeed = 1.4 / max(r, 0.35);
          float phase = ang + t * rotSpeed;

          // Multi-octave turbulence for streamlines and hot spots
          float n1 = fbm(vec2(phase * 1.6, r * 3.5 + t * 0.15));
          float n2 = fbm(vec2(phase * 5.0, r * 9.0 - t * 0.08));
          float streaks = 0.55 + n1 * 0.75 + n2 * 0.25;

          // Radial brightness — bright at ISCO, fades outward
          float radial = smoothstep(outerR, innerR, r);
          float isco = exp(-pow((r - innerR) / 0.18, 2.0)) * 1.8;

          // Doppler beaming — the side of the disk moving TOWARD the camera
          // is brighter and blueshifted. In our projected disk-space this
          // corresponds to one side of the horizontal axis (dp.x > 0).
          // Keep both sides visible; asymmetry is elegant, not extreme.
          float dop = 0.72 + 0.36 * clamp(dp.x / max(r, 0.3), -1.0, 1.0);

          float intensity = (radial * streaks + isco) * dop;

          // Physical-ish temperature: hot inner (white), warm mid (orange),
          // cool outer (deep magenta / blue-tinted)
          float t01 = smoothstep(innerR, outerR, r);
          vec3 hot  = vec3(1.00, 0.96, 0.86);
          vec3 mid  = vec3(1.00, 0.68, 0.32);
          vec3 cool = vec3(0.30, 0.18, 0.48);
          vec3 col = mix(hot, mid, smoothstep(0.0, 0.35, t01));
          col = mix(col, cool, smoothstep(0.6, 1.0, t01));

          // Blueshift on approaching side
          col = mix(col, vec3(0.8, 0.92, 1.15) * col, smoothstep(0.4, 1.0, dop));

          // Dark lanes
          float lane = smoothstep(0.4, 0.9, fbm(vec2(phase * 2.2, r * 3.0)));
          col *= mix(1.0, 0.6, lane * 0.6);

          col *= intensity * 0.9;

          return vec4(col, clamp(intensity, 0.0, 1.0));
        }

        // ---------- lensed arc (top halo) ----------
        // The bright band that appears to wrap OVER the event horizon —
        // the light of the far side of the disk bent around the sphere.
        // We render it directly in screen space as an emissive arc.
        vec3 lensedHalo(vec2 uv, float r, float t){
          if (uv.y < 0.02) return vec3(0.0);
          float bandR = 1.15;
          float bandSigma = 0.12;
          float arc = exp(-pow((r - bandR) / bandSigma, 2.0));
          // Fade the arc away at the sides (where it should merge with the
          // horizontal near-side disk)
          float sideFade = smoothstep(0.0, 0.25, uv.y);
          arc *= sideFade;

          // Streamlines around the arc
          float ang = atan(uv.y, uv.x);
          float phase = ang + t * 0.85;
          float streaks = 0.55 + fbm(vec2(phase * 3.0, r * 6.0 + t * 0.1)) * 0.9;

          // Doppler across the arc — brighter on the approaching side (right)
          // but visible all around
          float dop = 0.7 + 0.35 * clamp(cos(ang), -1.0, 1.0);

          vec3 hot = vec3(1.0, 0.9, 0.72);
          vec3 blueshift = vec3(0.85, 0.95, 1.15);
          vec3 col = mix(hot * 0.7, hot * blueshift, smoothstep(0.3, 1.0, dop));
          return col * arc * streaks * dop * 2.2;
        }

        void main(){
          // Screen coords, aspect-corrected so the SHORTER viewport axis
          // is always mapped to [-0.5, +0.5]. This lets us keep a stable
          // sphere size regardless of portrait/landscape orientation.
          vec2 p = (vUv - 0.5);
          if (uAspect > 1.0) {
            p.x *= uAspect;         // landscape: stretch x
          } else {
            p.y /= uAspect;         // portrait:  stretch y
          }

          // Sphere size relative to the shorter axis.
          //   Landscape: ~32% of viewport height
          //   Portrait:  ~30% of viewport width — leaves top/bottom room
          //              for counter, copy, CTA on tall phones
          float baseScale = (uAspect > 1.0) ? 0.32 : 0.30;
          float scale = mix(baseScale, baseScale * 0.88, uFocus);
          vec2 uv = p / scale;   // uv=(0,0) is centre, uv radius 1 = event horizon
          float r = length(uv);

          vec3 col = vec3(0.0);

          // ---------- Event horizon (pure black) ----------
          if (r < 1.0) {
            gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
            return;
          }

          // ---------- Photon ring — thin bright band right at r=1 ----------
          float ringR = 1.02;
          float photon = exp(-pow((r - ringR) / 0.018, 2.0)) * 1.0;

          // ---------- Near-side accretion disk (front of the sphere) ----------
          // The disk lies roughly in the horizontal plane. From this camera
          // angle it becomes a nearly horizontal band. We treat vertical
          // coordinate as "depth in the disk plane" through a squash.
          float diskSquash = 0.14;         // near edge-on
          vec2 diskUV = vec2(uv.x, uv.y / diskSquash);
          vec4 diskFront = sampleDisk(diskUV, uTime);
          // Only the FRONT of the disk (i.e. below the sphere's silhouette
          // in image space)
          diskFront *= smoothstep(-0.02, 0.08, -uv.y);

          // ---------- Lensed far-side halo ABOVE the horizon ----------
          vec3 halo = lensedHalo(uv, r, uTime);

          // ---------- Compose ----------
          col += diskFront.rgb;
          col += halo;
          col += vec3(1.0, 0.9, 0.75) * photon * 0.9;

          // Subtle gravitational "glow" leaking out from the horizon
          float outerGlow = exp(-pow((r - 1.0) / 0.5, 2.0)) * 0.05;
          col += vec3(0.9, 0.75, 0.55) * outerGlow;

          // Far falloff so distant regions read as dark sky
          float outFalloff = smoothstep(3.0, 5.5, r);
          col *= 1.0 - outFalloff * 0.95;

          float alpha = clamp(max(max(col.r, col.g), col.b), 0.0, 1.0);
          gl_FragColor = vec4(col, alpha);
        }
      `,
    });
  }, []);

  useFrame(({ clock, size: viewportSize }) => {
    material.uniforms.uTime.value = clock.getElapsedTime();
    material.uniforms.uResolution.value.set(viewportSize.width, viewportSize.height);
    material.uniforms.uAspect.value = viewportSize.width / viewportSize.height;
    size.current.w = viewportSize.width;
    size.current.h = viewportSize.height;
  });

  // Fullscreen quad (positions in clip-space via the vertex shader)
  return (
    <mesh frustumCulled={false} renderOrder={1}>
      <planeGeometry args={[2, 2, 1, 1]} />
      <primitive object={material} attach="material" />
    </mesh>
  );
}

/** Backdrop starfield — 3D points that provide parallax behind the hole. */
function StarField({ count = 900 }) {
  const positions = useMemo(() => {
    const a = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 18 + Math.random() * 40;
      const th = Math.random() * Math.PI * 2;
      const ph = Math.acos(2 * Math.random() - 1);
      a[i * 3 + 0] = r * Math.sin(ph) * Math.cos(th);
      a[i * 3 + 1] = r * Math.sin(ph) * Math.sin(th);
      a[i * 3 + 2] = -Math.abs(r * Math.cos(ph)) - 8;
    }
    return a;
  }, [count]);

  const ref = useRef();
  useFrame(({ clock }) => {
    if (ref.current) ref.current.rotation.y = clock.getElapsedTime() * 0.003;
  });

  return (
    <points ref={ref} renderOrder={0}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={positions.length / 3} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial size={0.055} color="#ffffff" transparent opacity={0.55} sizeAttenuation depthWrite={false} />
    </points>
  );
}

export default function BlackHole({ intensity = 1, focusing = false }) {
  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (reducedMotion) {
    return (
      <div
        data-testid="black-hole-fallback"
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          background:
            "radial-gradient(circle at 50% 50%, rgba(255,190,120,0.22), rgba(80,90,160,0.06) 22%, #000 55%)",
        }}
      />
    );
  }

  return (
    <div
      data-testid="black-hole-canvas"
      aria-hidden="true"
      style={{ position: "fixed", inset: 0, zIndex: 1 }}
    >
      <Canvas
        orthographic
        camera={{ position: [0, 0, 5], zoom: 1 }}
        dpr={[1, 1.8]}
        gl={{
          antialias: true,
          alpha: false,
          powerPreference: "high-performance",
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.05,
        }}
      >
        <color attach="background" args={["#000000"]} />
        <Suspense fallback={null}>
          <StarField />
          <BlackHoleSurface />
          <FocusUniform focusing={focusing} />
          <EffectComposer multisampling={0}>
            <Bloom
              intensity={0.7}
              luminanceThreshold={0.55}
              luminanceSmoothing={0.85}
              mipmapBlur
              kernelSize={KernelSize.LARGE}
            />
            <ChromaticAberration
              blendFunction={BlendFunction.NORMAL}
              offset={[0.0006, 0.0006]}
              radialModulation
              modulationOffset={0.35}
            />
            <Vignette eskil={false} offset={0.25} darkness={0.85} />
            <Noise premultiply blendFunction={BlendFunction.SCREEN} opacity={0.035} />
          </EffectComposer>
        </Suspense>
      </Canvas>
    </div>
  );
}

/** Eases the shader's uFocus uniform toward the current focusing target. */
function FocusUniform({ focusing }) {
  const value = useRef(0);
  useFrame(({ scene }) => {
    const target = focusing ? 1 : 0;
    value.current += (target - value.current) * 0.05;
    scene.traverse((o) => {
      if (o.material && o.material.uniforms && o.material.uniforms.uFocus) {
        o.material.uniforms.uFocus.value = value.current;
      }
    });
  });
  return null;
}
